import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import { createCloud } from "../api/clouds";
import { getEnterpriseId } from "../utils/authSession";

const PROVIDERS = [
  { value: "m365", label: "Microsoft 365 (Graph)" },
  { value: "azure", label: "Azure Resource Manager" },
  { value: "aws", label: "Amazon Web Services (AWS)" },
];

export default function CloudConnectDialog({ open, onClose, onSuccess, defaultProvider = "" }) {
  const [provider, setProvider] = useState(defaultProvider);
  
  // Azure/M365 fields
  const [tenantId, setTenantId] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  
  // AWS fields
  const [accessKeyId, setAccessKeyId] = useState("");
  const [secretAccessKey, setSecretAccessKey] = useState("");
  const [awsRegion, setAwsRegion] = useState("us-east-1");
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const enterprise_id = getEnterpriseId();

  // Reset provider when defaultProvider changes
  React.useEffect(() => {
    setProvider(defaultProvider);
  }, [defaultProvider]);

  function resetForm() {
    setProvider(defaultProvider || "");
    setTenantId("");
    setClientId("");
    setClientSecret("");
    setAccessKeyId("");
    setSecretAccessKey("");
    setAwsRegion("us-east-1");
    setError(null);
    setLoading(false);
  }

  async function handleSubmit(e) {
    e?.preventDefault();
    setError(null);

    if (provider === 'aws') {
      // AWS validation
      if (!accessKeyId || !secretAccessKey) {
        setError("Please fill AWS Access Key ID and Secret Access Key.");
        return;
      }

      if (accessKeyId.trim().length < 16) {
        setError("AWS Access Key ID appears invalid. It should be at least 16 characters.");
        return;
      }

      if (secretAccessKey.trim().length < 20) {
        setError("AWS Secret Access Key appears invalid. It should be at least 20 characters.");
        return;
      }
    } else {
      // Azure/M365 validation
      if (!tenantId || !clientId || !clientSecret) {
        setError("Please fill Tenant ID, Client ID and Client Secret.");
        return;
      }

      // Validate GUID format for Tenant ID and Client ID
      const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      
      if (!guidRegex.test(tenantId.trim())) {
        setError("Invalid Tenant ID format. Please enter a valid GUID (e.g., 12345678-1234-1234-1234-123456789abc). Note: Tenant ID cannot be an email address.");
        return;
      }

      if (!guidRegex.test(clientId.trim())) {
        setError("Invalid Client ID format. Please enter a valid GUID (e.g., 12345678-1234-1234-1234-123456789abc).");
        return;
      }

      if (clientSecret.trim().length < 10) {
        setError("Client Secret appears too short. Please verify you copied the complete secret value.");
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        enterpriseId: enterprise_id,
        provider,
      };

      if (provider === 'aws') {
        payload.accessKeyId = accessKeyId.trim();
        payload.secretAccessKey = secretAccessKey.trim();
        payload.awsRegion = awsRegion;
      } else {
        payload.tenantId = tenantId.trim();
        payload.clientId = clientId.trim();
        payload.clientSecret = clientSecret.trim();
      }

      console.log('Submitting cloud connection payload:', { ...payload, secretAccessKey: '***', clientSecret: '***' });
      const body = await createCloud(payload); // backend does token exchange and returns summary
      // `body` expected: { id, summary }
      onSuccess && onSuccess(body);
      resetForm();
      onClose && onClose();
    } catch (err) {
      const msg =
        err?.response?.data?.message || 
        err?.response?.data?.detail || 
        err?.response?.data?.error || 
        err?.message || 
        "Connection failed";
      setError(String(msg));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={open} onClose={() => { if (!loading) { resetForm(); onClose && onClose(); } }} maxWidth="sm" fullWidth>
      <DialogTitle>
        {!provider ? 'Choose Cloud Service' : `Connect ${provider === 'aws' ? 'AWS' : provider === 'azure' ? 'Azure' : 'Microsoft 365'}`}
      </DialogTitle>

      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: "grid", gap: 2 }}>
          {!provider ? (
            <>
              <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
                Select which cloud service you want to connect to your enterprise dashboard.
              </Alert>
              
              <TextField
                select
                label="Cloud Provider"
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
                fullWidth
                helperText="Choose your cloud service provider"
                autoFocus
              >
                {PROVIDERS.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </TextField>
            </>
          ) : provider === 'aws' ? (
            <>
              <Alert severity="info" sx={{ mt: 1 }}>
                You'll need an AWS IAM user with programmatic access and permissions to read EC2, S3, RDS, Lambda, and Cost Explorer data.
              </Alert>
              
              <TextField
                label="AWS Access Key ID"
                value={accessKeyId}
                onChange={(e) => setAccessKeyId(e.target.value)}
                fullWidth
                size="small"
                placeholder="AKIAIOSFODNN7EXAMPLE"
                helperText="Your AWS Access Key ID (starts with AKIA)"
              />

              <TextField
                label="AWS Secret Access Key"
                value={secretAccessKey}
                onChange={(e) => setSecretAccessKey(e.target.value)}
                fullWidth
                size="small"
                placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
                type="password"
                helperText="Your AWS Secret Access Key"
              />

              <TextField
                select
                label="Primary AWS Region"
                value={awsRegion}
                onChange={(e) => setAwsRegion(e.target.value)}
                fullWidth
                size="small"
                helperText="Select your primary AWS region for monitoring"
              >
                <MenuItem value="us-east-1">US East (N. Virginia)</MenuItem>
                <MenuItem value="us-east-2">US East (Ohio)</MenuItem>
                <MenuItem value="us-west-1">US West (N. California)</MenuItem>
                <MenuItem value="us-west-2">US West (Oregon)</MenuItem>
                <MenuItem value="eu-west-1">EU (Ireland)</MenuItem>
                <MenuItem value="eu-west-2">EU (London)</MenuItem>
                <MenuItem value="eu-central-1">EU (Frankfurt)</MenuItem>
                <MenuItem value="ap-south-1">Asia Pacific (Mumbai)</MenuItem>
                <MenuItem value="ap-southeast-1">Asia Pacific (Singapore)</MenuItem>
                <MenuItem value="ap-southeast-2">Asia Pacific (Sydney)</MenuItem>
                <MenuItem value="ap-northeast-1">Asia Pacific (Tokyo)</MenuItem>
              </TextField>

              <Typography variant="body2" color="text.secondary">
                After submission, we will validate your credentials and fetch data from your AWS account including EC2 instances, S3 buckets, RDS databases, and cost information.
              </Typography>
            </>
          ) : (
            <>
              <TextField
                label="Tenant ID"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                fullWidth
                size="small"
                placeholder="12345678-1234-1234-1234-123456789abc"
                helperText="Azure AD Tenant ID (must be a GUID, not an email address)"
                error={tenantId && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(tenantId.trim())}
              />

              <TextField
                label="Client ID (Application ID)"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                fullWidth
                size="small"
                placeholder="a0b1c2d3-e4f5-6789-0123-456789abcdef"
                helperText="Application (client) ID from Azure AD app registration"
                error={clientId && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(clientId.trim())}
              />

              <TextField
                label="Client Secret"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                fullWidth
                size="small"
                placeholder="Paste the client secret value"
                type="password"
                helperText="Client secret value (not the secret ID)"
              />

              <Typography variant="body2" color="text.secondary">
                After submission we will attempt a client-credentials token exchange and fetch basic summary (tenants, subscriptions, user/ license counts).
              </Typography>
            </>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => { if (!loading) { resetForm(); onClose && onClose(); } }} disabled={loading}>
          Cancel
        </Button>

        {!provider ? (
          <Button
            variant="contained"
            disabled={true}
            sx={{ minWidth: 120 }}
          >
            Select Provider
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{ minWidth: 120 }}
          >
            {loading ? (
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} color="inherit" /> Connecting…
              </Box>
            ) : (
              "Connect"
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}